import './paypal.less'

import {
	PayPalButtons,
	PayPalScriptProvider
} from '@paypal/react-paypal-js/dist/esm/react-paypal-js.js'
import requestService from 'mithril/request/request'
import {RegistrationModel} from 'models/registration/RegistrationModel'
import {ConfirmationData} from 'models/registration/steps/SummaryStepModel'
import React from 'react'
import useReactRouter from 'use-react-router'
import {useTranslation} from 'util/i18n'
import {style} from 'util/style'
import {Theme} from 'views/theme'

const styles = style('paypal')

const request = requestService(window, Promise).request

const updateConfirmation = (
	orderId: string,
	status: string,
	error?: string
) => {
	request({
		method: 'PUT',
		url: `/api/register/${orderId}/update`,
		data: {payment_status: status, browser_error: error}
	}).catch(error => {
		throw error
	})
}

export const Paypal: React.FC<{
	registration: RegistrationModel
	confirmation: ConfirmationData
	setIsChecked: (isChecked: boolean) => void
	updateRegistration: (model: RegistrationModel) => void
	setErrorMessage: (message: string) => void
}> = ({
	registration,
	confirmation,
	setIsChecked,
	updateRegistration,
	setErrorMessage
}) => {
	const {history} = useReactRouter()
	const t = useTranslation()
	const step = registration.allSteps.summary
	// tslint:disable:no-string-literal
	const clientID: string = window['__PAYPAL_CLIENT_ID__']
	const currency = 'EUR'

	const dataLayer = {
		event: 'purchase',
		ecommerce: {
			item_list_id: registration.config.url.substring(1).replace(/\//g, '_'),
			item_list_name: registration.event.title,
			value: registration.totalValue,
			items: [
				...registration.packs.map(pack => ({
					item_category: 'event',
					item_id: pack.config.key,
					item_name: pack.config.label,
					price: pack.config.price
				})),
				...registration.options
					.map(option => {
						if (!option) return null
						const selected = option.getChoiceFor(registration)
						if (selected) {
							return {
								item_category: option.key,
								item_id: selected.key,
								item_name: selected.label,
								price: selected.config.price
							}
						}
					})
					.filter(i => Boolean(i))
			]
		}
	}

	return (
		<PayPalScriptProvider
			options={{
				'client-id': clientID,
				currency
			}}
		>
			<Theme.Wysiwyg {...styles()}>{step.config.paypal}</Theme.Wysiwyg>
			<PayPalButtons
				disabled={
					!step.state.agree_privacy ||
					!step.state.agree_terms ||
					(step.shouldShowNoShow && !step.state.agree_no_show)
				}
				style={{
					shape: 'rect',
					tagline: false
				}}
				onClick={() => setIsChecked(true)}
				createOrder={(_, actions) =>
					createOrder(
						confirmation,
						registration,
						currency,
						actions,
						true
					).catch(error => {
						if (error.message.includes('INVALID_REQUEST')) {
							return createOrder(
								confirmation,
								registration,
								currency,
								actions,
								false
							)
						}
					})
				}
				// Workaround for following issue: https://github.com/paypal/paypal-checkout-components/issues/1550
				onShippingChange={async () => {
					// this forces a popup to appear instead of inline...
				}}
				onApprove={(_, actions) => {
					return actions.order.capture().then(_ => {
						// Purchase dataLayer
						window.dataLayer = window.dataLayer || []
						window.dataLayer.push(dataLayer)

						// Clear registration form
						updateRegistration(registration.clear())
						// Update confirmation payment status
						updateConfirmation(confirmation.payment.order_id, 'pending')
						// Show thank you page
						history.push(step.thanksUrl)
					})
				}}
				onCancel={_ =>
					// Update confirmation payment status
					updateConfirmation(
						confirmation.payment.order_id,
						'unregistered_canceled'
					)
				}
				onError={error => {
					// Show error message
					setErrorMessage(t('error'))
					// Update confirmation payment status
					updateConfirmation(
						confirmation.payment.order_id,
						'unregistered_errored',
						error.message
					)
				}}
			/>
		</PayPalScriptProvider>
	)
}

const createOrder = async (
	confirmation: ConfirmationData,
	registration: RegistrationModel,
	currency: string,
	actions,
	withPayerDetails: boolean
) => {
	const payerDetails = registration.state.details
	const personalDetails = withPayerDetails && {
		name: {
			given_name: payerDetails.firstname,
			surname: payerDetails.lastname
		},
		email_address: payerDetails.email
	}
	const phoneDetails = payerDetails.phone && {
		phone: {
			phone_type: 'MOBILE',
			phone_number: {
				national_number: payerDetails.phone
			}
		}
	}
	const addressDetails = payerDetails.not_affiliated
		? {
				address: {
					country_code: payerDetails.country
				}
		  }
		: {
				address: {
					address_line_1:
						payerDetails.invoice_address ||
						(payerDetails.street &&
							payerDetails.number &&
							`${payerDetails.street} ${payerDetails.number}`),
					address_line_2:
						payerDetails.invoice_address_line2 || payerDetails.box,
					admin_area_1: payerDetails.invoice_state || payerDetails.state,
					admin_area_2: payerDetails.invoice_city || payerDetails.city,
					postal_code:
						payerDetails.invoice_postal_code || payerDetails.postal_code,
					country_code: payerDetails.invoice_country || payerDetails.country
				}
		  }
	const payerDetailsOjbect = withPayerDetails && {
		payer: {
			...personalDetails,
			...phoneDetails,
			...addressDetails
		}
	}

	const orderData = {
		purchase_units: [
			{
				amount: {
					value: `${confirmation.price.incl}`,
					breakdown: {
						item_total: {
							currency_code: currency,
							value: `${confirmation.price.excl}`
						},

						tax_total: {
							currency_code: currency,
							value: `${confirmation.price.vat}`
						}
					}
				},
				reference_id: confirmation.payment.order_id,
				description: registration.event.title
			}
		],
		application_context: {
			shipping_preference: 'NO_SHIPPING'
		}
	}
	return actions.order.create({
		...orderData,
		...payerDetailsOjbect
	})
}
